import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Home,
  Plus,
  LogOut,
  CalendarIcon,
  Upload,
  X,
  Eye,
  Image,
  Loader2,
  CheckCircle,
  User,
  Clock,
  Check,
  CreditCard,
  Banknote,
  Building,
  Wallet,
  Store,
  ShoppingBag,
  Search,
  Users,
  MessageSquare,
  ArrowUpDown,
  ClipboardList,
  Bell,
  BellOff,
  AlertCircle,
  MapPin,
  Phone,
  Edit
} from 'lucide-react';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import { subscribeToPush, checkPushStatus } from '../utils/pushNotifications';
import { compressImages } from '../utils/imageCompression';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get photo URL (handles both external and local paths)
const getPhotoUrl = (filePath) => {
  if (!filePath) return '';
  // If it's already a full URL, return as-is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }
  // Otherwise, prepend the backend URL
  return `${process.env.REACT_APP_BACKEND_URL}${filePath}`;
};

// Payment method labels
const PAYMENT_METHODS = {
  pending_deposit: { label: 'Αναμονή Κατάθεσης', icon: Clock },
  deposit: { label: 'Κατάθεση', icon: Building },
  card: { label: 'Κάρτα', icon: CreditCard },
  iris: { label: 'Iris', icon: Wallet },
  cash: { label: 'Μετρητά', icon: Banknote }
};

// Job type labels
const JOB_TYPES = {
  wholesale: { label: 'Χονδρική', icon: Store },
  retail: { label: 'Λιανική', icon: ShoppingBag }
};

const TechnicianDashboard = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('list'); // list, create, browse, assignments
  const fileInputRef = useRef(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [browseSearchQuery, setBrowseSearchQuery] = useState('');
  
  // Sort state
  const [sortOrder, setSortOrder] = useState('newest');
  const [browseSortOrder, setBrowseSortOrder] = useState('newest');

  // Assignments state
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  
  // Assignment completion state
  const [showCompletionForm, setShowCompletionForm] = useState(false);
  const [completionForm, setCompletionForm] = useState({
    work_performed: '',
    recommendations: '',
    status: 'completed',
    status_notes: '',
    job_type: 'retail',
    payment_method: 'cash',
    payment_comments: '',
    needs_invoice: false,
    customer_afm: '',
    customer_email: ''
  });
  const [completionFiles, setCompletionFiles] = useState([]);
  const [submittingCompletion, setSubmittingCompletion] = useState(false);
  const completionFileInputRef = useRef(null);

  // Push notifications state
  const [pushStatus, setPushStatus] = useState({ supported: false, subscribed: false });
  const [enablingPush, setEnablingPush] = useState(false);

  // Job form state
  const [jobForm, setJobForm] = useState({
    customer_name: '',
    customer_phone: '',
    address_street: '',
    address_city: '',
    address_postal: '',
    visit_date: new Date(),
    description: '',
    work_performed: '',
    recommendations: '',
    status: 'pending',
    status_notes: '',
    job_type: 'retail',
    payment_method: 'cash',
    payment_comments: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Job detail
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Edit job state (technicians can edit description and add photos)
  const [editJobDialogOpen, setEditJobDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [editJobForm, setEditJobForm] = useState({
    description: '',
    work_performed: '',
    recommendations: '',
    payment_comments: ''
  });
  const [editJobFiles, setEditJobFiles] = useState([]);
  const [savingJob, setSavingJob] = useState(false);
  const editJobFileInputRef = useRef(null);

  // Check push status on mount
  useEffect(() => {
    checkPushStatus().then(setPushStatus);
  }, []);

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    if (activeView === 'browse') {
      fetchAllJobs();
    }
    if (activeView === 'assignments') {
      fetchAssignments();
    }
  }, [activeView, browseSearchQuery]);

  // Sort my jobs when sortOrder changes
  useEffect(() => {
    if (jobs.length > 0) {
      const sortedJobs = [...jobs].sort((a, b) => {
        switch (sortOrder) {
          case 'oldest':
            return new Date(a.created_at) - new Date(b.created_at);
          case 'newest':
            return new Date(b.created_at) - new Date(a.created_at);
          case 'date_asc':
            return a.visit_date.localeCompare(b.visit_date);
          case 'date_desc':
            return b.visit_date.localeCompare(a.visit_date);
          case 'customer_asc':
            return a.customer_name.localeCompare(b.customer_name, 'el');
          case 'customer_desc':
            return b.customer_name.localeCompare(a.customer_name, 'el');
          default:
            return new Date(b.created_at) - new Date(a.created_at);
        }
      });
      setJobs(sortedJobs);
    }
  }, [sortOrder]);

  // Sort all jobs when browseSortOrder changes
  useEffect(() => {
    if (allJobs.length > 0) {
      const sortedJobs = [...allJobs].sort((a, b) => {
        switch (browseSortOrder) {
          case 'oldest':
            return new Date(a.created_at) - new Date(b.created_at);
          case 'newest':
            return new Date(b.created_at) - new Date(a.created_at);
          case 'date_asc':
            return a.visit_date.localeCompare(b.visit_date);
          case 'date_desc':
            return b.visit_date.localeCompare(a.visit_date);
          case 'customer_asc':
            return a.customer_name.localeCompare(b.customer_name, 'el');
          case 'customer_desc':
            return b.customer_name.localeCompare(a.customer_name, 'el');
          default:
            return new Date(b.created_at) - new Date(a.created_at);
        }
      });
      setAllJobs(sortedJobs);
    }
  }, [browseSortOrder]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      const res = await axios.get(`${API}/jobs?${params.toString()}`);
      setJobs(res.data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllJobs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (browseSearchQuery) params.append('search', browseSearchQuery);
      const res = await axios.get(`${API}/jobs/all/browse?${params.toString()}`);
      setAllJobs(res.data);
    } catch (error) {
      console.error('Failed to fetch all jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/assignments`);
      setAssignments(res.data);
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnablePush = async () => {
    setEnablingPush(true);
    try {
      const result = await subscribeToPush(token);
      if (result.success) {
        setPushStatus({ supported: true, subscribed: true });
        alert('Οι ειδοποιήσεις ενεργοποιήθηκαν!');
      } else if (result.error === 'permission_denied') {
        alert('Πρέπει να επιτρέψετε τις ειδοποιήσεις στον browser σας.');
      } else {
        alert('Δεν ήταν δυνατή η ενεργοποίηση ειδοποιήσεων.');
      }
    } catch (error) {
      console.error('Push subscription error:', error);
    } finally {
      setEnablingPush(false);
    }
  };

  const handleUpdateAssignmentStatus = async (assignmentId, newStatus) => {
    // If changing to in_progress or completed, show completion form
    if (newStatus === 'in_progress' || newStatus === 'completed') {
      // Pre-fill form with assignment data
      if (selectedAssignment) {
        setCompletionForm({
          work_performed: '',
          recommendations: '',
          status: newStatus === 'in_progress' ? 'pending' : 'completed',
          status_notes: '',
          job_type: selectedAssignment.job_type || 'retail',
          payment_method: selectedAssignment.payment_method || 'cash',
          payment_comments: selectedAssignment.payment_comments || '',
          needs_invoice: selectedAssignment.needs_invoice || false,
          customer_afm: selectedAssignment.customer_afm || '',
          customer_email: selectedAssignment.customer_email || ''
        });
        setCompletionFiles([]);
        setShowCompletionForm(true);
      }
      return;
    }
    
    // For other status changes (like back to assigned)
    try {
      await axios.patch(`${API}/assignments/${assignmentId}/status`, { status: newStatus });
      setAssignments(prev => prev.map(a => 
        a.id === assignmentId ? { ...a, status: newStatus } : a
      ));
      if (selectedAssignment?.id === assignmentId) {
        setSelectedAssignment(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      alert('Σφάλμα ενημέρωσης κατάστασης');
    }
  };

  const handleCompletionFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      const validExtensions = ['jpg', 'jpeg', 'png', 'heic', 'heif', 'webp'];
      const isValidExt = validExtensions.includes(ext) || f.type.startsWith('image/');
      return isValidExt && f.size <= 15 * 1024 * 1024;
    });
    
    if (validFiles.length === 0 && files.length > 0) {
      alert('Μη υποστηριζόμενος τύπος αρχείου. Υποστηρίζονται: JPG, PNG, HEIC');
      return;
    }
    
    try {
      // Compress images before adding (includes HEIC conversion)
      const compressedFiles = await compressImages(validFiles);
      setCompletionFiles(prev => [...prev, ...compressedFiles]);
    } catch (error) {
      console.error('File processing error:', error);
      alert('Σφάλμα επεξεργασίας φωτογραφίας: ' + (error.message || 'Άγνωστο σφάλμα'));
    }
  };

  const removeCompletionFile = (index) => {
    setCompletionFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitCompletion = async (e) => {
    e.preventDefault();
    if (!selectedAssignment) return;
    
    setSubmittingCompletion(true);
    try {
      // Convert assignment to job
      const response = await axios.post(`${API}/assignments/${selectedAssignment.id}/convert-to-job`, {
        work_performed: completionForm.work_performed,
        recommendations: completionForm.recommendations,
        status: completionForm.status,
        status_notes: completionForm.status_notes,
        job_type: completionForm.job_type,
        payment_method: completionForm.payment_method,
        payment_comments: completionForm.payment_comments,
        needs_invoice: completionForm.needs_invoice,
        customer_afm: completionForm.customer_afm,
        customer_email: completionForm.customer_email
      });
      
      const newJobId = response.data.job_id;
      
      // Upload compressed photos if any
      if (completionFiles.length > 0) {
        for (const file of completionFiles) {
          const formData = new FormData();
          formData.append('file', file);
          await axios.post(`${API}/jobs/${newJobId}/photos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      }
      
      // Reset and close
      setShowCompletionForm(false);
      setAssignmentDialogOpen(false);
      setCompletionFiles([]);
      setCompletionForm({
        work_performed: '',
        recommendations: '',
        status: 'completed',
        status_notes: '',
        job_type: 'retail',
        payment_method: 'cash',
        payment_comments: '',
        needs_invoice: false,
        customer_afm: '',
        customer_email: ''
      });
      
      // Refresh data
      fetchAssignments();
      fetchJobs();
      
      alert('Η εργασία καταχωρήθηκε επιτυχώς!');
      
    } catch (error) {
      alert(error.response?.data?.detail || 'Σφάλμα κατά την καταχώρηση');
    } finally {
      setSubmittingCompletion(false);
    }
  };

  const openInMaps = (address) => {
    const query = encodeURIComponent(`${address.street}, ${address.city} ${address.postal}`);
    // Check if iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      window.open(`maps://maps.apple.com/?q=${query}`, '_blank');
    } else {
      window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs();
  };

  const handleBrowseSearch = (e) => {
    e.preventDefault();
    fetchAllJobs();
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      // Create job
      const jobData = {
        ...jobForm,
        visit_date: format(jobForm.visit_date, 'yyyy-MM-dd')
      };
      const jobRes = await axios.post(`${API}/jobs`, jobData);
      const jobId = jobRes.data.id;

      // Upload photos
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/jobs/${jobId}/photos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      // Reset form
      setJobForm({
        customer_name: '',
        customer_phone: '',
        address_street: '',
        address_city: '',
        address_postal: '',
        visit_date: new Date(),
        description: '',
        work_performed: '',
        recommendations: '',
        status: 'pending',
        status_notes: '',
        job_type: 'retail',
        payment_method: 'cash',
        payment_comments: ''
      });
      setSelectedFiles([]);
      setActiveView('list');
      fetchJobs();
      alert('Η εργασία αποθηκεύτηκε επιτυχώς!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Σφάλμα αποθήκευσης');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      // Support HEIC/HEIF from iPhone, plus standard formats
      const validExtensions = ['jpg', 'jpeg', 'png', 'heic', 'heif', 'webp'];
      const isValidExt = validExtensions.includes(ext) || f.type.startsWith('image/');
      return isValidExt && f.size <= 15 * 1024 * 1024; // Increased to 15MB for iPhone photos
    });
    
    if (validFiles.length === 0 && files.length > 0) {
      alert('Μη υποστηριζόμενος τύπος αρχείου. Υποστηρίζονται: JPG, PNG, HEIC, HEIF, WebP');
      return;
    }
    
    // Show loading indicator
    setUploading(true);
    try {
      // Compress images before adding (includes HEIC conversion)
      const compressedFiles = await compressImages(validFiles);
      if (compressedFiles.length > 0) {
        setSelectedFiles([...selectedFiles, ...compressedFiles]);
      } else if (validFiles.length > 0) {
        alert('Δεν ήταν δυνατή η επεξεργασία των φωτογραφιών. Δοκιμάστε διαφορετικό format.');
      }
    } catch (error) {
      console.error('File processing error:', error);
      alert('Σφάλμα επεξεργασίας φωτογραφίας: ' + (error.message || 'Άγνωστο σφάλμα'));
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  // Edit job handlers for technicians
  const handleEditJob = (job) => {
    setEditingJob(job);
    setEditJobForm({
      description: job.description || '',
      work_performed: job.work_performed || '',
      recommendations: job.recommendations || '',
      payment_comments: job.payment_comments || ''
    });
    setEditJobFiles([]);
    setEditJobDialogOpen(true);
  };

  const handleEditJobFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      const validExtensions = ['jpg', 'jpeg', 'png', 'heic', 'heif', 'webp'];
      const isValidExt = validExtensions.includes(ext) || f.type.startsWith('image/');
      return isValidExt && f.size <= 15 * 1024 * 1024;
    });
    
    if (validFiles.length === 0 && files.length > 0) {
      alert('Μη υποστηριζόμενος τύπος αρχείου');
      return;
    }
    
    try {
      // Compress images before adding (includes HEIC conversion)
      const compressedFiles = await compressImages(validFiles);
      setEditJobFiles(prev => [...prev, ...compressedFiles]);
    } catch (error) {
      console.error('File processing error:', error);
      alert('Σφάλμα επεξεργασίας φωτογραφίας');
    }
  };

  const removeEditJobFile = (index) => {
    setEditJobFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveJobEdit = async (e) => {
    e.preventDefault();
    if (!editingJob) return;
    
    setSavingJob(true);
    try {
      // Update job description/work fields and payment comments
      await axios.patch(`${API}/jobs/${editingJob.id}`, {
        description: editJobForm.description,
        work_performed: editJobForm.work_performed,
        recommendations: editJobForm.recommendations,
        payment_comments: editJobForm.payment_comments
      });
      
      // Upload new photos if any
      if (editJobFiles.length > 0) {
        for (const file of editJobFiles) {
          const formData = new FormData();
          formData.append('file', file);
          await axios.post(`${API}/jobs/${editingJob.id}/photos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      }
      
      // Close dialog and refresh
      setEditJobDialogOpen(false);
      setEditingJob(null);
      setEditJobFiles([]);
      fetchJobs();
      alert('Η εργασία ενημερώθηκε!');
      
    } catch (error) {
      alert(error.response?.data?.detail || 'Σφάλμα ενημέρωσης');
    } finally {
      setSavingJob(false);
    }
  };

  const handleViewJob = async (jobId, isBrowse = false) => {
    try {
      const endpoint = isBrowse ? `${API}/jobs/all/browse/${jobId}` : `${API}/jobs/${jobId}`;
      const res = await axios.get(endpoint);
      setSelectedJob({ ...res.data, isBrowseView: isBrowse });
      setJobDialogOpen(true);
    } catch (error) {
      alert('Σφάλμα φόρτωσης εργασίας');
    }
  };

  const handleUpdateStatus = async (jobId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.patch(`${API}/jobs/${jobId}/status`, { status: newStatus });
      // Update local state
      setSelectedJob(prev => ({ ...prev, status: newStatus }));
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: newStatus } : j));
    } catch (error) {
      alert('Σφάλμα ενημέρωσης κατάστασης');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusBadge = (status) => {
    if (status === 'completed') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
          <Check className="w-3 h-3" /> Ολοκληρώθηκε
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <Clock className="w-3 h-3" /> Εκκρεμής
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <header className="bg-slate-900 text-white p-4 sticky top-0 z-40">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-chivo font-bold uppercase text-sm">Τεχνικός</h1>
              <p className="text-slate-400 text-xs">{user?.name}</p>
            </div>
          </div>
          <Button
            data-testid="tech-logout-btn"
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-slate-400 hover:text-white"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto p-4">
        {/* Logo Banner */}
        <div className="bg-slate-900 rounded-xl p-4 mb-4 flex items-center justify-center">
          <img src="/logo-white.png" alt="Raptopoulos" className="h-10" />
        </div>

        {/* Jobs List View */}
        {activeView === 'list' && (
          <div className="animate-fade-in">
            <h2 className="font-chivo text-xl font-bold text-slate-900 uppercase mb-4">
              Οι Εργασίες Μου
            </h2>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  data-testid="search-my-jobs"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-rugged pl-11 pr-20"
                  placeholder="Αναζήτηση..."
                />
                <Button
                  type="submit"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 bg-orange-500 hover:bg-orange-600 text-white"
                >
                  Εύρεση
                </Button>
              </div>
            </form>

            {/* Sort Filter */}
            <div className="flex items-center gap-2 mb-4 bg-white p-3 rounded-lg border-2 border-slate-200">
              <ArrowUpDown className="w-4 h-4 text-slate-500" />
              <Label className="text-sm font-bold uppercase text-slate-500">Ταξινόμηση:</Label>
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger data-testid="my-jobs-sort-order" className="flex-1 h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Νεότερες πρώτα</SelectItem>
                  <SelectItem value="oldest">Παλαιότερες πρώτα</SelectItem>
                  <SelectItem value="date_desc">Ημ/νία επίσκεψης (↓)</SelectItem>
                  <SelectItem value="date_asc">Ημ/νία επίσκεψης (↑)</SelectItem>
                  <SelectItem value="customer_asc">Πελάτης (Α-Ω)</SelectItem>
                  <SelectItem value="customer_desc">Πελάτης (Ω-Α)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Image className="w-10 h-10 text-slate-400" />
                </div>
                <p className="text-slate-500 mb-4">{searchQuery ? 'Δεν βρέθηκαν αποτελέσματα' : 'Δεν υπάρχουν εργασίες ακόμα'}</p>
                {!searchQuery && (
                  <Button
                    data-testid="empty-create-job-btn"
                    className="btn-orange"
                    onClick={() => setActiveView('create')}
                  >
                    <Plus className="w-5 h-5 mr-2" /> Νέα Εργασία
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {jobs.map((job) => (
                  <div
                    key={job.id}
                    className="job-card cursor-pointer"
                    onClick={() => handleViewJob(job.id)}
                    data-testid={`job-card-${job.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-bold text-slate-900">{job.customer_name}</h3>
                        <p className="text-sm text-slate-500">{job.address_street}, {job.address_city}</p>
                        <div className="mt-2">
                          {getStatusBadge(job.status)}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-slate-900">{job.visit_date}</p>
                        <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                          <Image className="w-3 h-3" />
                          {job.photos?.length || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Browse All Jobs View */}
        {activeView === 'browse' && (
          <div className="animate-fade-in">
            <h2 className="font-chivo text-xl font-bold text-slate-900 uppercase mb-4">
              Όλες οι Δουλειές
            </h2>

            {/* Search Bar */}
            <form onSubmit={handleBrowseSearch} className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  data-testid="search-all-jobs"
                  value={browseSearchQuery}
                  onChange={(e) => setBrowseSearchQuery(e.target.value)}
                  className="input-rugged pl-11 pr-20"
                  placeholder="Αναζήτηση πελάτη, τεχνικού, σύστασης..."
                />
                <Button
                  type="submit"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 bg-orange-500 hover:bg-orange-600 text-white"
                >
                  Εύρεση
                </Button>
              </div>
            </form>

            {/* Sort Filter */}
            <div className="flex items-center gap-2 mb-4 bg-white p-3 rounded-lg border-2 border-slate-200">
              <ArrowUpDown className="w-4 h-4 text-slate-500" />
              <Label className="text-sm font-bold uppercase text-slate-500">Ταξινόμηση:</Label>
              <Select value={browseSortOrder} onValueChange={setBrowseSortOrder}>
                <SelectTrigger data-testid="browse-jobs-sort-order" className="flex-1 h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Νεότερες πρώτα</SelectItem>
                  <SelectItem value="oldest">Παλαιότερες πρώτα</SelectItem>
                  <SelectItem value="date_desc">Ημ/νία επίσκεψης (↓)</SelectItem>
                  <SelectItem value="date_asc">Ημ/νία επίσκεψης (↑)</SelectItem>
                  <SelectItem value="customer_asc">Πελάτης (Α-Ω)</SelectItem>
                  <SelectItem value="customer_desc">Πελάτης (Ω-Α)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : allJobs.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-slate-400" />
                </div>
                <p className="text-slate-500">{browseSearchQuery ? 'Δεν βρέθηκαν αποτελέσματα' : 'Δεν υπάρχουν εργασίες'}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {allJobs.map((job) => (
                  <div
                    key={job.id}
                    className="bg-white border-2 border-slate-200 rounded-xl p-4 cursor-pointer hover:border-orange-300 transition-colors"
                    onClick={() => handleViewJob(job.id, true)}
                    data-testid={`browse-job-${job.id}`}
                  >
                    {/* Header with technician */}
                    <div className="flex items-center gap-2 mb-3 pb-3 border-b border-slate-100">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-900 text-sm">{job.technician_name}</p>
                        <p className="text-xs text-slate-500">{job.visit_date}</p>
                      </div>
                      <div className="ml-auto">
                        {getStatusBadge(job.status)}
                      </div>
                    </div>

                    {/* Customer & Address */}
                    <div className="mb-3">
                      <h3 className="font-bold text-slate-900">{job.customer_name}</h3>
                      <p className="text-sm text-slate-500">{job.address_street}, {job.address_city}</p>
                    </div>

                    {/* Recommendations */}
                    {job.recommendations && (
                      <div className="mb-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-xs font-bold uppercase text-amber-700 mb-1 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" /> Σύσταση
                        </p>
                        <p className="text-sm text-slate-700 line-clamp-2">{job.recommendations}</p>
                      </div>
                    )}

                    {/* Photos count */}
                    {job.photos && job.photos.length > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="flex -space-x-2">
                          {job.photos.slice(0, 3).map((photo, idx) => (
                            <img
                              key={photo.id}
                              src={`${getPhotoUrl(photo.file_path)}`}
                              alt=""
                              className="w-10 h-10 rounded-lg object-cover border-2 border-white"
                            />
                          ))}
                        </div>
                        {job.photos.length > 3 && (
                          <span className="text-xs text-slate-500">+{job.photos.length - 3} ακόμα</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Assignments View */}
        {activeView === 'assignments' && (
          <div className="animate-fade-in">
            <h2 className="font-chivo text-xl font-bold text-slate-900 uppercase mb-4">
              Οι Αναθέσεις Μου
            </h2>

            {/* Push Notifications Banner */}
            {pushStatus.supported && !pushStatus.subscribed && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-3">
                  <Bell className="w-8 h-8 text-blue-500" />
                  <div className="flex-1">
                    <p className="font-bold text-slate-900">Ενεργοποιήστε τις ειδοποιήσεις</p>
                    <p className="text-sm text-slate-600">Για να λαμβάνετε ειδοποίηση όταν σας ανατίθεται νέα εργασία</p>
                  </div>
                  <Button
                    data-testid="enable-push-btn"
                    onClick={handleEnablePush}
                    disabled={enablingPush}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    {enablingPush ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ενεργοποίηση'}
                  </Button>
                </div>
              </div>
            )}

            {pushStatus.subscribed && (
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-3 mb-4 flex items-center gap-2">
                <Check className="w-5 h-5 text-emerald-500" />
                <span className="text-sm text-emerald-700">Οι ειδοποιήσεις είναι ενεργοποιημένες</span>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : assignments.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ClipboardList className="w-10 h-10 text-slate-400" />
                </div>
                <p className="text-slate-500">Δεν υπάρχουν αναθέσεις</p>
              </div>
            ) : (
              <div className="space-y-4">
                {assignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    className={`bg-white border-2 rounded-xl p-4 cursor-pointer transition-all ${
                      assignment.status === 'assigned' 
                        ? 'border-orange-300 shadow-md' 
                        : assignment.status === 'completed'
                        ? 'border-emerald-200'
                        : 'border-slate-200'
                    }`}
                    onClick={() => {
                      setSelectedAssignment(assignment);
                      setAssignmentDialogOpen(true);
                    }}
                    data-testid={`assignment-${assignment.id}`}
                  >
                    {/* Priority Badge */}
                    {assignment.priority === 'high' && (
                      <div className="flex items-center gap-1 text-red-600 text-sm font-bold mb-2">
                        <AlertCircle className="w-4 h-4" /> ΥΨΗΛΗ ΠΡΟΤΕΡΑΙΟΤΗΤΑ
                      </div>
                    )}

                    {/* Status & Date */}
                    <div className="flex justify-between items-start mb-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        assignment.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        assignment.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {assignment.status === 'completed' ? 'Ολοκληρώθηκε' : 
                         assignment.status === 'in_progress' ? 'Σε εξέλιξη' : 'Νέα Ανάθεση'}
                      </span>
                      <div className="text-right">
                        <span className="text-sm text-slate-500">{assignment.visit_date}</span>
                        {assignment.visit_time && (
                          <div className="text-sm font-bold text-blue-600">{assignment.visit_time}</div>
                        )}
                      </div>
                    </div>

                    {/* Customer Info */}
                    <h3 className="font-bold text-slate-900 text-lg">{assignment.customer_name}</h3>
                    
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        openInMaps({
                          street: assignment.address_street,
                          city: assignment.address_city,
                          postal: assignment.address_postal || ''
                        });
                      }}
                      className="flex items-center gap-2 text-blue-600 mt-2 hover:underline"
                    >
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">{assignment.address_street}, {assignment.address_city}</span>
                    </button>

                    <div className="flex items-center gap-2 text-slate-500 mt-1">
                      <Phone className="w-4 h-4" />
                      <a href={`tel:${assignment.customer_phone}`} className="text-sm text-blue-600">{assignment.customer_phone}</a>
                    </div>

                    {/* Payment Info Preview */}
                    {(assignment.job_type || assignment.payment_method) && (
                      <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                        <span className="px-2 py-0.5 bg-slate-100 rounded">{assignment.job_type === 'wholesale' ? 'Χονδρική' : 'Λιανική'}</span>
                        <span className="px-2 py-0.5 bg-slate-100 rounded">
                          {assignment.payment_method === 'cash' ? 'Μετρητά' :
                           assignment.payment_method === 'card' ? 'Κάρτα' :
                           assignment.payment_method === 'iris' ? 'IRIS' :
                           assignment.payment_method === 'deposit' ? 'Κατάθεση' : 'Αναμονή'}
                        </span>
                      </div>
                    )}

                    {/* Description Preview */}
                    {assignment.description && (
                      <p className="text-sm text-slate-600 mt-3 line-clamp-2 bg-slate-50 p-2 rounded">
                        {assignment.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Create Job View */}
        {activeView === 'create' && (
          <div className="animate-fade-in">
            <div className="flex items-center gap-4 mb-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveView('list')}
                className="text-slate-500"
              >
                ← Πίσω
              </Button>
              <h2 className="font-chivo text-xl font-bold text-slate-900 uppercase">
                Νέα Εργασία
              </h2>
            </div>

            <form onSubmit={handleCreateJob} className="space-y-5">
              {/* Customer Info */}
              <div className="bg-white p-4 rounded-xl border-2 border-slate-200">
                <h3 className="font-chivo font-bold text-slate-700 uppercase text-sm mb-4">
                  Στοιχεία Πελάτη
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Ονοματεπώνυμο *</Label>
                    <Input
                      data-testid="job-customer-name"
                      value={jobForm.customer_name}
                      onChange={(e) => setJobForm({ ...jobForm, customer_name: e.target.value })}
                      className="input-rugged"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Τηλέφωνο *</Label>
                    <Input
                      data-testid="job-customer-phone"
                      type="tel"
                      value={jobForm.customer_phone}
                      onChange={(e) => setJobForm({ ...jobForm, customer_phone: e.target.value })}
                      className="input-rugged"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-white p-4 rounded-xl border-2 border-slate-200">
                <h3 className="font-chivo font-bold text-slate-700 uppercase text-sm mb-4">
                  Διεύθυνση
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Οδός *</Label>
                    <Input
                      data-testid="job-address-street"
                      value={jobForm.address_street}
                      onChange={(e) => setJobForm({ ...jobForm, address_street: e.target.value })}
                      className="input-rugged"
                      placeholder="π.χ. Σταδίου 25"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Πόλη *</Label>
                      <Input
                        data-testid="job-address-city"
                        value={jobForm.address_city}
                        onChange={(e) => setJobForm({ ...jobForm, address_city: e.target.value })}
                        className="input-rugged"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Τ.Κ.</Label>
                      <Input
                        data-testid="job-address-postal"
                        value={jobForm.address_postal}
                        onChange={(e) => setJobForm({ ...jobForm, address_postal: e.target.value })}
                        className="input-rugged"
                        placeholder="12345"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Date */}
              <div className="bg-white p-4 rounded-xl border-2 border-slate-200">
                <h3 className="font-chivo font-bold text-slate-700 uppercase text-sm mb-4">
                  Ημερομηνία Επίσκεψης
                </h3>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="job-visit-date"
                      variant="outline"
                      className="input-rugged w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(jobForm.visit_date, 'dd MMMM yyyy', { locale: el })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={jobForm.visit_date}
                      onSelect={(date) => date && setJobForm({ ...jobForm, visit_date: date })}
                      locale={el}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Job Details */}
              <div className="bg-white p-4 rounded-xl border-2 border-slate-200">
                <h3 className="font-chivo font-bold text-slate-700 uppercase text-sm mb-4">
                  Λεπτομέρειες Εργασίας
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Περιγραφή / Σχόλια</Label>
                    <Textarea
                      data-testid="job-description"
                      value={jobForm.description}
                      onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                      className="input-rugged min-h-[100px]"
                      placeholder="Περιγράψτε το πρόβλημα..."
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Εργασία που εκτελέστηκε *</Label>
                    <Textarea
                      data-testid="job-work-performed"
                      value={jobForm.work_performed}
                      onChange={(e) => setJobForm({ ...jobForm, work_performed: e.target.value })}
                      className="input-rugged min-h-[100px]"
                      placeholder="Τι εργασία κάνατε..."
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Συστάσεις προς Πελάτη</Label>
                    <Textarea
                      data-testid="job-recommendations"
                      value={jobForm.recommendations}
                      onChange={(e) => setJobForm({ ...jobForm, recommendations: e.target.value })}
                      className="input-rugged min-h-[80px]"
                      placeholder="Προτάσεις για μελλοντικές ενέργειες..."
                    />
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="bg-white p-4 rounded-xl border-2 border-slate-200">
                <h3 className="font-chivo font-bold text-slate-700 uppercase text-sm mb-4">
                  Κατάσταση Εργασίας
                </h3>
                <Select value={jobForm.status} onValueChange={(v) => setJobForm({ ...jobForm, status: v, status_notes: v === 'completed' ? '' : jobForm.status_notes })}>
                  <SelectTrigger data-testid="job-status-select" className="input-rugged">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-500" /> Εκκρεμής
                      </span>
                    </SelectItem>
                    <SelectItem value="completed">
                      <span className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-500" /> Ολοκληρώθηκε
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>

                {/* Status Notes - only show when pending */}
                {jobForm.status === 'pending' && (
                  <div className="mt-4">
                    <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Σχόλια Εκκρεμότητας</Label>
                    <Textarea
                      data-testid="job-status-notes"
                      value={jobForm.status_notes}
                      onChange={(e) => setJobForm({ ...jobForm, status_notes: e.target.value })}
                      className="input-rugged min-h-[80px]"
                      placeholder="Π.χ. Αναμένουμε ανταλλακτικό, θα επιστρέψω Δευτέρα..."
                    />
                  </div>
                )}
              </div>

              {/* Job Type - Wholesale/Retail */}
              <div className="bg-white p-4 rounded-xl border-2 border-slate-200">
                <h3 className="font-chivo font-bold text-slate-700 uppercase text-sm mb-4">
                  Τύπος Δουλειάς
                </h3>
                <RadioGroup 
                  value={jobForm.job_type} 
                  onValueChange={(v) => setJobForm({ ...jobForm, job_type: v })}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="retail" id="retail" data-testid="job-type-retail" />
                    <Label htmlFor="retail" className="flex items-center gap-2 cursor-pointer">
                      <ShoppingBag className="w-4 h-4 text-blue-500" />
                      Λιανική
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="wholesale" id="wholesale" data-testid="job-type-wholesale" />
                    <Label htmlFor="wholesale" className="flex items-center gap-2 cursor-pointer">
                      <Store className="w-4 h-4 text-purple-500" />
                      Χονδρική
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Payment Method */}
              <div className="bg-white p-4 rounded-xl border-2 border-slate-200">
                <h3 className="font-chivo font-bold text-slate-700 uppercase text-sm mb-4">
                  Μέθοδος Πληρωμής
                </h3>
                <Select value={jobForm.payment_method} onValueChange={(v) => setJobForm({ ...jobForm, payment_method: v })}>
                  <SelectTrigger data-testid="job-payment-method" className="input-rugged">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">
                      <span className="flex items-center gap-2">
                        <Banknote className="w-4 h-4 text-emerald-500" /> Μετρητά
                      </span>
                    </SelectItem>
                    <SelectItem value="card">
                      <span className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-blue-500" /> Κάρτα
                      </span>
                    </SelectItem>
                    <SelectItem value="iris">
                      <span className="flex items-center gap-2">
                        <Wallet className="w-4 h-4 text-purple-500" /> Iris
                      </span>
                    </SelectItem>
                    <SelectItem value="deposit">
                      <span className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-slate-500" /> Κατάθεση
                      </span>
                    </SelectItem>
                    <SelectItem value="pending_deposit">
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-500" /> Αναμονή Κατάθεσης
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>

                {/* Payment Comments */}
                <div className="mt-4">
                  <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Σχόλια Πληρωμής</Label>
                  <Textarea
                    data-testid="job-payment-comments"
                    value={jobForm.payment_comments}
                    onChange={(e) => setJobForm({ ...jobForm, payment_comments: e.target.value })}
                    className="input-rugged min-h-[80px]"
                    placeholder="Π.χ. Θα γίνει κατάθεση μέχρι Παρασκευή..."
                  />
                </div>
              </div>

              {/* Photos */}
              <div className="bg-white p-4 rounded-xl border-2 border-slate-200">
                <h3 className="font-chivo font-bold text-slate-700 uppercase text-sm mb-4">
                  Φωτογραφίες
                </h3>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.heic,.heif"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />

                <div
                  className="dropzone"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="photo-dropzone"
                >
                  <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">Πατήστε για να προσθέσετε φωτογραφίες</p>
                  <p className="text-slate-400 text-sm mt-1">JPG, PNG έως 10MB</p>
                </div>

                {selectedFiles.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="relative">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Preview ${index}`}
                          className="w-full aspect-square object-cover rounded-lg border-2 border-slate-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit */}
              <Button
                data-testid="submit-job-btn"
                type="submit"
                className="btn-orange w-full"
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Αποθήκευση...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" /> Αποθήκευση Εργασίας
                  </>
                )}
              </Button>
            </form>
          </div>
        )}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-nav">
        <button
          data-testid="nav-home-btn"
          onClick={() => setActiveView('list')}
          className={`flex flex-col items-center gap-1 p-2 ${activeView === 'list' ? 'text-orange-500' : 'text-slate-400'}`}
        >
          <Home className="w-5 h-5" />
          <span className="text-xs font-medium">Αρχική</span>
        </button>
        <button
          data-testid="nav-assignments-btn"
          onClick={() => setActiveView('assignments')}
          className={`flex flex-col items-center gap-1 p-2 ${activeView === 'assignments' ? 'text-orange-500' : 'text-slate-400'} relative`}
        >
          <ClipboardList className="w-5 h-5" />
          <span className="text-xs font-medium">Αναθέσεις</span>
          {assignments.filter(a => a.status === 'assigned').length > 0 && (
            <span className="absolute -top-1 right-0 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {assignments.filter(a => a.status === 'assigned').length}
            </span>
          )}
        </button>
        <button
          data-testid="nav-create-btn"
          onClick={() => setActiveView('create')}
          className={`flex flex-col items-center gap-1 p-2 ${activeView === 'create' ? 'text-orange-500' : 'text-slate-400'}`}
        >
          <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center -mt-6 shadow-lg">
            <Plus className="w-6 h-6 text-white" />
          </div>
          <span className="text-xs font-medium mt-1">Νέα</span>
        </button>
        <button
          data-testid="nav-browse-btn"
          onClick={() => setActiveView('browse')}
          className={`flex flex-col items-center gap-1 p-2 ${activeView === 'browse' ? 'text-orange-500' : 'text-slate-400'}`}
        >
          <Users className="w-5 h-5" />
          <span className="text-xs font-medium">Όλες</span>
        </button>
        <button
          data-testid="nav-profile-btn"
          onClick={handleLogout}
          className="flex flex-col items-center gap-1 p-2 text-slate-400"
        >
          <LogOut className="w-5 h-5" />
          <span className="text-xs font-medium">Έξοδος</span>
        </button>
      </nav>

      {/* Job Detail Dialog */}
      <Dialog open={jobDialogOpen} onOpenChange={setJobDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="font-chivo uppercase">Λεπτομέρειες Εργασίας</DialogTitle>
              {selectedJob && !selectedJob.isBrowseView && (
                <Button
                  data-testid="edit-my-job-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => { setJobDialogOpen(false); handleEditJob(selectedJob); }}
                  className="border-2 border-blue-500 text-blue-600 hover:bg-blue-50"
                >
                  <Edit className="w-4 h-4 mr-1" /> Επεξεργασία
                </Button>
              )}
            </div>
          </DialogHeader>
          {selectedJob && (
            <div className="space-y-4 mt-4">
              {/* Technician info for browse view */}
              {selectedJob.isBrowseView && (
                <div className="p-4 bg-slate-100 rounded-lg flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-500">Τεχνικός</p>
                    <p className="font-bold text-lg text-slate-900">{selectedJob.technician_name}</p>
                  </div>
                </div>
              )}

              {/* Status Banner */}
              <div className={`p-4 rounded-lg ${selectedJob.status === 'completed' ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-500">Κατάσταση</p>
                    <div className="mt-1">{getStatusBadge(selectedJob.status)}</div>
                  </div>
                  {!selectedJob.isBrowseView && (
                    <Select 
                      value={selectedJob.status} 
                      onValueChange={(v) => handleUpdateStatus(selectedJob.id, v)}
                      disabled={updatingStatus}
                    >
                      <SelectTrigger data-testid="update-status-select" className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Εκκρεμής</SelectItem>
                        <SelectItem value="completed">Ολοκληρώθηκε</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
                {/* Status Notes */}
                {selectedJob.status === 'pending' && selectedJob.status_notes && (
                  <div className="mt-3 p-3 bg-white rounded-lg border border-amber-300">
                    <p className="text-xs font-bold uppercase text-amber-700 mb-1">Σχόλια Εκκρεμότητας</p>
                    <p className="text-sm text-slate-700">{selectedJob.status_notes}</p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-bold uppercase text-slate-500">Πελάτης</p>
                  <p className="font-medium">{selectedJob.customer_name}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase text-slate-500">Τηλέφωνο</p>
                  <p className="font-medium">{selectedJob.customer_phone}</p>
                </div>
              </div>

              <div>
                <p className="text-xs font-bold uppercase text-slate-500">Διεύθυνση</p>
                <p className="font-medium">{selectedJob.address_street}, {selectedJob.address_city} {selectedJob.address_postal}</p>
              </div>

              <div>
                <p className="text-xs font-bold uppercase text-slate-500">Ημερομηνία</p>
                <p className="font-medium">{selectedJob.visit_date}</p>
              </div>

              <div>
                <p className="text-xs font-bold uppercase text-slate-500">Περιγραφή</p>
                <p className="bg-slate-50 p-3 rounded-lg text-sm">{selectedJob.description || '-'}</p>
              </div>

              <div>
                <p className="text-xs font-bold uppercase text-slate-500">Εργασία</p>
                <p className="bg-slate-50 p-3 rounded-lg text-sm">{selectedJob.work_performed || '-'}</p>
              </div>

              <div>
                <p className="text-xs font-bold uppercase text-slate-500">Συστάσεις</p>
                <p className="bg-amber-50 p-3 rounded-lg text-sm border border-amber-200">{selectedJob.recommendations || '-'}</p>
              </div>

              {/* Job Type & Payment Info */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-bold uppercase text-slate-500">Τύπος Δουλειάς</p>
                  <p className="font-medium flex items-center gap-2 mt-1">
                    {selectedJob.job_type === 'wholesale' ? (
                      <><Store className="w-4 h-4 text-purple-500" /> Χονδρική</>
                    ) : (
                      <><ShoppingBag className="w-4 h-4 text-blue-500" /> Λιανική</>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase text-slate-500">Μέθοδος Πληρωμής</p>
                  <p className="font-medium flex items-center gap-2 mt-1">
                    {PAYMENT_METHODS[selectedJob.payment_method]?.icon && (
                      (() => {
                        const Icon = PAYMENT_METHODS[selectedJob.payment_method].icon;
                        return <Icon className="w-4 h-4" />;
                      })()
                    )}
                    {PAYMENT_METHODS[selectedJob.payment_method]?.label || selectedJob.payment_method}
                  </p>
                </div>
              </div>

              {selectedJob.payment_comments && (
                <div>
                  <p className="text-xs font-bold uppercase text-slate-500">Σχόλια Πληρωμής</p>
                  <p className="bg-amber-50 p-3 rounded-lg text-sm border border-amber-200">{selectedJob.payment_comments}</p>
                </div>
              )}

              {selectedJob.photos && selectedJob.photos.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase text-slate-500 mb-2">Φωτογραφίες ({selectedJob.photos.length})</p>
                  <div className="photo-grid">
                    {selectedJob.photos.map((photo) => (
                      <a
                        key={photo.id}
                        href={`${getPhotoUrl(photo.file_path)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={`${getPhotoUrl(photo.file_path)}`}
                          alt="Job photo"
                          className="cursor-pointer hover:opacity-80 transition-opacity"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assignment Detail Dialog */}
      <Dialog open={assignmentDialogOpen} onOpenChange={(open) => {
        setAssignmentDialogOpen(open);
        if (!open) {
          setShowCompletionForm(false);
          setCompletionFiles([]);
        }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-chivo uppercase">
              {showCompletionForm ? 'Καταχώρηση Εργασίας' : 'Λεπτομέρειες Ανάθεσης'}
            </DialogTitle>
          </DialogHeader>
          {selectedAssignment && !showCompletionForm && (
            <div className="space-y-4 mt-4">
              {/* Priority Banner */}
              {selectedAssignment.priority === 'high' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <span className="font-bold text-red-700">ΥΨΗΛΗ ΠΡΟΤΕΡΑΙΟΤΗΤΑ</span>
                </div>
              )}

              {/* Status Badge */}
              {selectedAssignment.converted_to_job_id ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    <span className="font-bold text-emerald-700">Ολοκληρώθηκε - Καταχωρήθηκε στις εργασίες</span>
                  </div>
                </div>
              ) : (
                <div className={`p-4 rounded-lg ${
                  selectedAssignment.status === 'in_progress' ? 'bg-blue-50 border border-blue-200' :
                  'bg-amber-50 border border-amber-200'
                }`}>
                  <p className="text-xs font-bold uppercase text-slate-500 mb-2">Κατάσταση</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    selectedAssignment.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {selectedAssignment.status === 'in_progress' ? 'Σε εξέλιξη' : 'Νέα Ανάθεση'}
                  </span>
                </div>
              )}

              {/* Customer Info */}
              <div className="bg-white border-2 border-slate-200 rounded-lg p-4">
                <h3 className="font-chivo font-bold text-lg text-slate-900 mb-3">Στοιχεία Πελάτη</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-slate-400" />
                    <span className="font-medium">{selectedAssignment.customer_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-5 h-5 text-slate-400" />
                    <a href={`tel:${selectedAssignment.customer_phone}`} className="text-blue-600 font-medium">
                      {selectedAssignment.customer_phone}
                    </a>
                  </div>
                  <button 
                    onClick={() => openInMaps({
                      street: selectedAssignment.address_street,
                      city: selectedAssignment.address_city,
                      postal: selectedAssignment.address_postal || ''
                    })}
                    className="flex items-start gap-2 text-blue-600 hover:underline"
                  >
                    <MapPin className="w-5 h-5 mt-0.5" />
                    <div className="text-left">
                      <p className="font-medium">{selectedAssignment.address_street}</p>
                      <p className="text-slate-500">{selectedAssignment.address_city} {selectedAssignment.address_postal}</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Visit Date & Time */}
              <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg">
                <CalendarIcon className="w-5 h-5 text-slate-500" />
                <div className="flex-1">
                  <p className="text-xs font-bold uppercase text-slate-500">Ημερομηνία & Ώρα Επίσκεψης</p>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{selectedAssignment.visit_date}</p>
                    {selectedAssignment.visit_time && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded font-bold text-sm">
                        {selectedAssignment.visit_time}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <p className="text-xs font-bold uppercase text-slate-500 mb-2">Περιγραφή Εργασίας</p>
                <p className="bg-slate-50 p-4 rounded-lg text-slate-700">{selectedAssignment.description}</p>
              </div>

              {/* Payment Info */}
              {(selectedAssignment.job_type || selectedAssignment.payment_method) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-xs font-bold uppercase text-slate-500 mb-2">Πληροφορίες Πληρωμής</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-white rounded text-sm">
                      {selectedAssignment.job_type === 'wholesale' ? '🏪 Χονδρική' : '🛒 Λιανική'}
                    </span>
                    <span className="px-2 py-1 bg-white rounded text-sm">
                      {selectedAssignment.payment_method === 'cash' ? '💵 Μετρητά' :
                       selectedAssignment.payment_method === 'card' ? '💳 Κάρτα' :
                       selectedAssignment.payment_method === 'iris' ? '📱 IRIS' :
                       selectedAssignment.payment_method === 'deposit' ? '🏦 Κατάθεση' : '⏳ Αναμονή'}
                    </span>
                  </div>
                  {selectedAssignment.payment_comments && (
                    <p className="mt-2 text-sm text-slate-600">{selectedAssignment.payment_comments}</p>
                  )}
                </div>
              )}

              {/* Notes */}
              {selectedAssignment.notes && (
                <div>
                  <p className="text-xs font-bold uppercase text-slate-500 mb-2">Σημειώσεις</p>
                  <p className="bg-amber-50 p-4 rounded-lg text-slate-700 border border-amber-200">
                    {selectedAssignment.notes}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              {!selectedAssignment.converted_to_job_id && (
                <div className="border-t pt-4 space-y-2">
                  <Button
                    data-testid="start-work-btn"
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                    onClick={() => handleUpdateAssignmentStatus(selectedAssignment.id, 'in_progress')}
                  >
                    <Clock className="w-5 h-5 mr-2" /> Ξεκίνησα την Εργασία
                  </Button>
                  <Button
                    data-testid="complete-work-btn"
                    className="w-full btn-orange"
                    onClick={() => handleUpdateAssignmentStatus(selectedAssignment.id, 'completed')}
                  >
                    <CheckCircle className="w-5 h-5 mr-2" /> Ολοκλήρωση & Καταχώρηση
                  </Button>
                </div>
              )}

              {/* Assigned by */}
              <div className="text-sm text-slate-500 border-t pt-3">
                <p>Ανάθεση από: <span className="font-medium">{selectedAssignment.created_by_name}</span></p>
                <p>Ημ/νία: {new Date(selectedAssignment.created_at).toLocaleDateString('el-GR')}</p>
              </div>
            </div>
          )}

          {/* Completion Form */}
          {selectedAssignment && showCompletionForm && (
            <form onSubmit={handleSubmitCompletion} className="space-y-4 mt-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-bold text-slate-900">{selectedAssignment.customer_name}</p>
                <p className="text-sm text-slate-500">{selectedAssignment.address_street}, {selectedAssignment.address_city}</p>
              </div>

              {/* Work Performed */}
              <div>
                <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Εργασία που Εκτελέστηκε *</Label>
                <Textarea
                  data-testid="completion-work-performed"
                  value={completionForm.work_performed}
                  onChange={(e) => setCompletionForm({ ...completionForm, work_performed: e.target.value })}
                  className="input-rugged min-h-[100px]"
                  placeholder="Περιγράψτε τι εργασία κάνατε..."
                  required
                />
              </div>

              {/* Recommendations */}
              <div>
                <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Συστάσεις</Label>
                <Textarea
                  data-testid="completion-recommendations"
                  value={completionForm.recommendations}
                  onChange={(e) => setCompletionForm({ ...completionForm, recommendations: e.target.value })}
                  className="input-rugged"
                  placeholder="Συστάσεις για τον πελάτη..."
                />
              </div>

              {/* Status */}
              <div>
                <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Κατάσταση Εργασίας</Label>
                <Select value={completionForm.status} onValueChange={(v) => setCompletionForm({ ...completionForm, status: v })}>
                  <SelectTrigger data-testid="completion-status" className="input-rugged">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completed">Ολοκληρώθηκε</SelectItem>
                    <SelectItem value="pending">Εκκρεμεί</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {completionForm.status === 'pending' && (
                <div>
                  <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Λόγος Εκκρεμότητας</Label>
                  <Input
                    data-testid="completion-status-notes"
                    value={completionForm.status_notes}
                    onChange={(e) => setCompletionForm({ ...completionForm, status_notes: e.target.value })}
                    className="input-rugged"
                    placeholder="π.χ. Αναμονή ανταλλακτικού..."
                  />
                </div>
              )}

              {/* Payment Info */}
              <div className="border-t pt-4">
                <h4 className="font-bold text-slate-900 mb-3">Πληρωμή</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Τύπος</Label>
                    <Select value={completionForm.job_type} onValueChange={(v) => setCompletionForm({ ...completionForm, job_type: v })}>
                      <SelectTrigger className="input-rugged">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="retail">Λιανική</SelectItem>
                        <SelectItem value="wholesale">Χονδρική</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Τρόπος</Label>
                    <Select value={completionForm.payment_method} onValueChange={(v) => setCompletionForm({ ...completionForm, payment_method: v })}>
                      <SelectTrigger className="input-rugged">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Μετρητά</SelectItem>
                        <SelectItem value="card">Κάρτα</SelectItem>
                        <SelectItem value="iris">IRIS</SelectItem>
                        <SelectItem value="deposit">Κατάθεση</SelectItem>
                        <SelectItem value="pending_deposit">Αναμονή</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-2">
                  <Input
                    data-testid="completion-payment-comments"
                    value={completionForm.payment_comments}
                    onChange={(e) => setCompletionForm({ ...completionForm, payment_comments: e.target.value })}
                    className="input-rugged"
                    placeholder="Σχόλια πληρωμής..."
                  />
                </div>
              </div>

              {/* Invoice Checkbox */}
              <div className="border-t pt-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={completionForm.needs_invoice}
                    onChange={(e) => setCompletionForm({ 
                      ...completionForm, 
                      needs_invoice: e.target.checked,
                      customer_afm: e.target.checked ? completionForm.customer_afm : '',
                      customer_email: e.target.checked ? completionForm.customer_email : ''
                    })}
                    className="w-5 h-5 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                    data-testid="completion-needs-invoice"
                  />
                  <span className="font-medium text-slate-700">Χρειάζεται Τιμολόγιο</span>
                </label>
              </div>

              {/* Invoice Fields */}
              {completionForm.needs_invoice && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h5 className="text-sm font-bold uppercase text-blue-700 mb-3">Στοιχεία Τιμολογίου</h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">ΑΦΜ</Label>
                      <Input
                        data-testid="completion-customer-afm"
                        value={completionForm.customer_afm}
                        onChange={(e) => setCompletionForm({ ...completionForm, customer_afm: e.target.value })}
                        className="input-rugged"
                        placeholder="ΑΦΜ πελάτη"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Email</Label>
                      <Input
                        data-testid="completion-customer-email"
                        type="email"
                        value={completionForm.customer_email}
                        onChange={(e) => setCompletionForm({ ...completionForm, customer_email: e.target.value })}
                        className="input-rugged"
                        placeholder="Email πελάτη"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Photo Upload */}
              <div className="border-t pt-4">
                <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Φωτογραφίες</Label>
                <input
                  type="file"
                  ref={completionFileInputRef}
                  onChange={handleCompletionFileSelect}
                  accept="image/*"
                  multiple
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-dashed border-2"
                  onClick={() => completionFileInputRef.current?.click()}
                >
                  <Upload className="w-5 h-5 mr-2" /> Προσθήκη Φωτογραφιών
                </Button>
                {completionFiles.length > 0 && (
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {completionFiles.map((file, index) => (
                      <div key={index} className="relative">
                        <img
                          src={URL.createObjectURL(file)}
                          alt=""
                          className="w-full h-20 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeCompletionFile(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowCompletionForm(false)}
                >
                  Πίσω
                </Button>
                <Button
                  type="submit"
                  className="flex-1 btn-orange"
                  disabled={submittingCompletion}
                >
                  {submittingCompletion ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Αποθήκευση...</>
                  ) : (
                    <><Check className="w-5 h-5 mr-2" /> Καταχώρηση</>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Job Dialog (Technician - limited fields) */}
      <Dialog open={editJobDialogOpen} onOpenChange={setEditJobDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-chivo uppercase">Επεξεργασία Εργασίας</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveJobEdit} className="space-y-4 mt-4">
            <p className="text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
              Μπορείτε να επεξεργαστείτε την περιγραφή και να προσθέσετε φωτογραφίες.
            </p>

            {/* Description */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Περιγραφή Εργασίας</Label>
              <Textarea
                data-testid="edit-job-description"
                value={editJobForm.description}
                onChange={(e) => setEditJobForm({ ...editJobForm, description: e.target.value })}
                className="input-rugged min-h-[100px]"
                placeholder="Περιγράψτε την εργασία..."
              />
            </div>

            {/* Work Performed */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Εργασία που Εκτελέστηκε</Label>
              <Textarea
                data-testid="edit-job-work-performed"
                value={editJobForm.work_performed}
                onChange={(e) => setEditJobForm({ ...editJobForm, work_performed: e.target.value })}
                className="input-rugged min-h-[100px]"
                placeholder="Περιγράψτε τι κάνατε..."
              />
            </div>

            {/* Recommendations */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Προτάσεις / Σημειώσεις</Label>
              <Textarea
                data-testid="edit-job-recommendations"
                value={editJobForm.recommendations}
                onChange={(e) => setEditJobForm({ ...editJobForm, recommendations: e.target.value })}
                className="input-rugged min-h-[80px]"
                placeholder="Σημειώσεις ή προτάσεις..."
              />
            </div>

            {/* Payment Comments */}
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Σχόλια Πληρωμής</Label>
              <Textarea
                data-testid="edit-job-payment-comments"
                value={editJobForm.payment_comments}
                onChange={(e) => setEditJobForm({ ...editJobForm, payment_comments: e.target.value })}
                className="input-rugged min-h-[60px]"
                placeholder="Σχόλια για την πληρωμή..."
              />
            </div>

            {/* Photo Upload */}
            <div className="border-t pt-4">
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">Προσθήκη Φωτογραφιών</Label>
              <input
                type="file"
                ref={editJobFileInputRef}
                onChange={handleEditJobFileSelect}
                accept="image/*"
                multiple
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                className="w-full border-dashed border-2"
                onClick={() => editJobFileInputRef.current?.click()}
              >
                <Upload className="w-5 h-5 mr-2" /> Προσθήκη Φωτογραφιών
              </Button>
              {editJobFiles.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {editJobFiles.map((file, index) => (
                    <div key={index} className="relative">
                      <img
                        src={URL.createObjectURL(file)}
                        alt=""
                        className="w-full h-20 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeEditJobFile(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-400 mt-2">
                Οι φωτογραφίες θα συμπιεστούν αυτόματα για εξοικονόμηση χώρου.
              </p>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full btn-rugged"
              disabled={savingJob}
            >
              {savingJob ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Αποθήκευση...</>
              ) : (
                <><Check className="w-5 h-5 mr-2" /> Αποθήκευση Αλλαγών</>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TechnicianDashboard;
